import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { FollowButton } from "@/components/FollowButton";
import { HeroPrayerVideo } from "@/components/HeroPrayerVideo";

const NAME_LINES = ["Charles", "McKuhn"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const STATS = [
  { value: "3", label: "Published Books" },
  { value: "50+", label: "Community Projects" },
  { value: "120+", label: "Talks & Revivals" },
];

export const Hero = ({ scrollToId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const { content } = useSettings();

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-ink text-parchment grain overflow-hidden flex items-center pt-28 pb-16"
    >
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 78% 20%, rgba(197,160,89,0.18), transparent 45%), radial-gradient(circle at 15% 85%, rgba(56,189,248,0.06), transparent 40%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold-light">
              Apostle · Prophetic Intercessor
            </span>
          </motion.div>

          <h1 className="font-serif font-semibold leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl">
            {NAME_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-parchment/75"
          >
            {content?.hero_tagline ??
              "Apostle, prophetic intercessor, author, and community leader — flowing under the authority and unction of the Holy Ghost, devoted to faith, revival, and the next generation."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-books"
              onClick={() => scrollToId("books")}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-ink font-semibold hover:bg-gold-light transition-colors"
            >
              <BookOpen size={18} />
              Explore Books & Writings
            </button>
            <button
              data-testid="hero-book-engagement"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full border border-parchment/30 text-parchment font-semibold hover:border-gold hover:text-gold-light transition-colors"
            >
              Book a Speaking / Revival
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <FollowButton />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label}`}>
                <div className="font-serif text-3xl sm:text-4xl font-semibold gold-foil">
                  {s.value}
                </div>
                <div className="mt-1 text-xs sm:text-sm text-parchment/60 leading-tight">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-3 border border-gold/40 rounded-[2rem] translate-x-4 translate-y-4" />
            <motion.div
              style={{ y: portraitY, scale: portraitScale }}
              className="relative overflow-hidden rounded-[2rem] border border-gold/30"
            >
              <HeroPrayerVideo />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
